<template>
  <!-- Pass From/To Information - Displays the source and destination locations with approval info -->
  <div class="d-flex justify-content-around px-4 px-xl-5">
    <!-- FROM Section -->
    <div class="from-cnt d-flex flex-column">
      <!-- From header -->
      <span
        class="text-white pb-2 fw-bold"
        :class="[isPassLongRunning || isInQueue ? 'text-black' : '']"
      >
        {{ $t("common.from") }}:
      </span>

      <!-- From location name and action button if applicable -->
      <div
        :class="[
          getFromClass,
          (isPassLongRunning && !fromButtonAction) || isInQueue
            ? 'text-border-black'
            : ''
        ]"
        @click="
          fromButtonAction ? $emit('open-stuff-modal', actions.approved) : null
        "
        data-test-id="student-active-pass-from"
      >
        {{ getNameDisplay(pass?.from) }}
        <i v-if="fromButtonAction" class="fi flaticon-check-1 ms-1" />
      </div>

      <!-- Approval timestamp -->
      <div
        v-if="pass?.approved_at"
        class="approved-at text-white p-2 text-center"
        :class="[isPassLongRunning ? 'text-black' : '']"
        data-test-id="student-active-pass-approved-at"
      >
        {{ formatDate(pass?.approved_at) }}
      </div>

      <!-- Approved By Info -->
      <FromApprovedByInfo :pass="pass" :is-system-ended="isPassSystemEnded" />
    </div>

    <!-- TO Section -->
    <div class="to-cnt d-flex flex-column">
      <!-- To header -->
      <span
        class="text-white pb-2 fw-bold"
        :class="[isPassLongRunning || isInQueue ? 'text-black' : '']"
      >
        {{ $t("common.to") }}:
      </span>

      <!-- To location name and action button if applicable -->
      <div
        :class="[
          getToClass,
          (isPassLongRunning && !toButtonAction) || isInQueue
            ? 'text-border-black'
            : ''
        ]"
        @click="toButtonAction ? $emit('open-stuff-modal', actions.end) : null"
        data-test-id="student-active-pass-to"
      >
        {{ getNameDisplay(pass?.to) }}
        <i v-if="toButtonAction" class="fi flaticon-check-1 ms-1" />
      </div>

      <!-- Completion timestamp -->
      <div
        v-if="pass?.completed_at"
        class="approved-at text-white p-2 text-center"
        :class="[isPassLongRunning || isInQueue ? 'text-black' : '']"
        data-test-id="student-active-pass-completed-at"
      >
        {{ formatDate(pass?.completed_at) }}
      </div>

      <!-- Completed By Info -->
      <ToCompletedByInfo :pass="pass" :is-system-ended="isPassSystemEnded" />
    </div>
  </div>
</template>

<script>
import { defineAsyncComponent } from "vue"

export default {
  name: "PassFromToInfo",
  components: {
    FromApprovedByInfo: defineAsyncComponent(
      () => import("./FromApprovedByInfo.vue")
    ),
    ToCompletedByInfo: defineAsyncComponent(
      () => import("./ToCompletedByInfo.vue")
    )
  }
}
</script>

<script setup>
import { computed } from "vue"
import helpers from "@/helpers/index"

const props = defineProps({
  pass: {
    type: Object,
    default: () => ({})
  },
  currentStatus: {
    type: String,
    default: ""
  },
  isPassLongRunning: {
    type: Boolean,
    default: false
  },
  isPassSystemEnded: {
    type: Boolean,
    default: false
  },
  actions: {
    type: Object,
    default: () => ({})
  }
})

defineEmits(["open-stuff-modal"])

// Determine if the pass is in the queue
const isInQueue = computed(() => props.currentStatus === "queue")

// Determine if From button should be clickable
const fromButtonAction = computed(() => {
  return (
    props.currentStatus === "waiting" ||
    (props.currentStatus === "approved" && !props.pass?.approved_by_user)
  )
})

// Determine if To button should be clickable
const toButtonAction = computed(() => {
  return props.currentStatus === "approved" && props.pass?.approved_by_user
})

// Class for From button
const getFromClass = computed(() => {
  if (fromButtonAction.value) {
    return "ft-data bg-white text-red"
  }
  return "ft-data text-white"
})

// Class for To button
const getToClass = computed(() => {
  if (toButtonAction.value) {
    return "ft-data text-green bg-white"
  }
  return "ft-data text-white"
})

// Format entity name for display
const getNameDisplay = (entity) => {
  if (!entity) return ""
  return entity?.name
    ? entity.name
    : `${entity?.first_name || ""} ${entity?.last_name || ""}`.trim()
}

// Format date for display
const formatDate = (date) => {
  return helpers.transformDate?.(date, "hh:mm A") || date
}
</script>
